import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import { textStyles } from '../../../../theme/config/typography';
import { FieldMode, FieldSize } from '../enums';
import { StyledInputProps } from './types';

const HEIGHT: Record<FieldSize, string> = {
  [FieldSize.Small]: '38px',
  [FieldSize.Big]: '2.75rem',
};

const PREFIX_W: Record<FieldSize, string> = {
  [FieldSize.Small]: '2.75rem',
  [FieldSize.Big]: '3.25rem',
};

const SUFFIX_W: Record<FieldSize, string> = {
  [FieldSize.Small]: '2.75rem',
  [FieldSize.Big]: '3.25rem',
};

const modeStyles = (mode: FieldMode, isError?: boolean) => {
  const borderDefault =
    mode === FieldMode.Tertiary
      ? 'rgba(9,15,56,0.2)'
      : 'var(--line)';
  const borderFocus =
    mode === FieldMode.Tertiary ? 'var(--line-2)' : 'var(--brand-green)';
  const borderError = 'var(--danger)';
  const bg =
    mode === FieldMode.Primary
      ? 'var(--surface)'
      : mode === FieldMode.Secondary
      ? 'var(--paper-2)'
      : 'rgba(255,255,255,0.2)';

  return css`
    background-color: ${bg};
    color: var(--ink);
    border-color: ${isError ? borderError : borderDefault};

    &:focus {
      border-color: ${isError ? borderError : borderFocus};
      box-shadow: 0 0 0 3px rgba(118, 177, 89, 0.15);
      ${mode === FieldMode.Tertiary &&
        css`
          background-color: ${borderFocus};
        `}
    }
  `;
};

export const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  height: ${({ inputSize = FieldSize.Small }) => HEIGHT[inputSize]};
  border-radius: ${({ isRounded, inputSize = FieldSize.Small }) =>
    isRounded
      ? inputSize === FieldSize.Small
        ? '1.25rem'
        : '3.125rem'
      : 'var(--r-sm)'};
  border-width: 1px;
  border-style: solid;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;

  ${({ theme, hasPrefix, hasSuffix, inputSize = FieldSize.Small }) =>
    theme.direction === 'rtl'
      ? css`
          padding-right: ${hasPrefix ? PREFIX_W[inputSize] : '0.875rem'};
          padding-left: ${hasSuffix ? SUFFIX_W[inputSize] : '0.875rem'};
        `
      : css`
          padding-left: ${hasPrefix ? PREFIX_W[inputSize] : '0.875rem'};
          padding-right: ${hasSuffix ? SUFFIX_W[inputSize] : '0.875rem'};
        `}

  ${({ mode = FieldMode.Primary, isError }) => modeStyles(mode, isError)}

  ${variant({ variants: { ...textStyles } })}
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease;
  font-size: 0.875rem;
  letter-spacing: -0.005em;

  &:disabled,
  &[disabled] {
    pointer-events: none;
    opacity: 0.5;
  }
`;

export const StyledInputPrefix = styled.div<StyledInputProps>`
  position: absolute;
  top: 0;
  ${({ theme }) => (theme.direction === 'rtl' ? 'right: 0;' : 'left: 0;')}
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${({ inputSize = FieldSize.Small }) => PREFIX_W[inputSize]};
  z-index: 1;
  color: var(--muted);
`;

export const StyledInputSuffix = styled.div<StyledInputProps>`
  position: absolute;
  top: 0;
  ${({ theme }) => (theme.direction === 'rtl' ? 'left: 0;' : 'right: 0;')}
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${({ inputSize = FieldSize.Small }) => SUFFIX_W[inputSize]};
  z-index: 1;
  color: var(--muted);
`;

export const StyledInputWrapper = styled.div<StyledInputProps>`
  width: 100%;
  position: relative;
`;
